import {attr, controller, target, targets} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller
export class CodespacesCalculatorElement extends HTMLElement {
  @targets declare machineSizes: HTMLInputElement[]
  get selectedMachineSize() {
    return this.machineSizes.find(machineSizeOption => machineSizeOption.checked)
  }
  get machineSize() {
    return (this.selectedMachineSize && this.selectedMachineSize.value) || ''
  }
  get machineSizeLabel() {
    return (this.selectedMachineSize && this.selectedMachineSize.getAttribute('data-label')) || ''
  }

  @target declare usageHoursPerDevInput: HTMLInputElement
  get usageHoursPerDev() {
    return parseInt(this.usageHoursPerDevInput.value) || 0
  }

  @target declare storedCountInput: HTMLInputElement
  get storedCount() {
    return parseInt(this.storedCountInput.value) || 0
  }

  @target declare projectSizeInput: HTMLInputElement
  get projectSize() {
    return parseInt(this.projectSizeInput.value) || 0
  }

  @target declare developerCountInput: HTMLInputElement
  get developerCount() {
    return parseInt(this.developerCountInput.value) || 0
  }

  @targets declare machineSizeLabels: HTMLElement[]
  @target declare costPerMonthDisplay: HTMLElement
  @target declare costPerDevPerMonthDisplay: HTMLElement

  @attr costPerMonth = 0
  @attr costPerDevPerMonth = 0
  @attr active = false

  declare unit_data: UnitData

  connectedCallback() {
    this.unit_data = getUnitData()
  }

  machineSizeUpdated(event: Event) {
    for (const machineSizelabel of this.machineSizeLabels) {
      machineSizelabel.classList.remove('gradient-border')
    }
    const machineSize = event.currentTarget as HTMLInputElement
    machineSize.closest('label')!.classList.add('gradient-border')
    this.updatePerDevPerMonth()
  }

  updatePerDevPerMonth() {
    const machineSizeUnitPrice = this.unit_data.hasOwnProperty(this.machineSize)
      ? this.unit_data[this.machineSize]!['value']
      : 0

    this.costPerDevPerMonth = this.calculatePerDevPerMonthCost(
      machineSizeUnitPrice,
      this.usageHoursPerDev,
      this.storedCount,
      this.projectSize,
    )
  }

  updatePerMonth() {
    this.costPerMonth = this.calculatePerMonthCost(this.developerCount, this.costPerDevPerMonth)
  }

  calculatePerDevPerMonthCost(
    machineSizeUnitPrice: number,
    usageHoursPerDev: number,
    storedCount: number,
    projectSize: number,
  ): number {
    const projectStorageUnitPrice = this.unit_data.codespaces_project_size!.value

    return ((usageHoursPerDev * 52) / 12) * machineSizeUnitPrice + storedCount * projectSize * projectStorageUnitPrice
  }

  calculatePerMonthCost(developerCount: number, costPerDevPerMonth: number): number {
    return developerCount * costPerDevPerMonth
  }

  displayCostPerDevPerMonth() {
    this.costPerDevPerMonthDisplay.textContent = currencyFormatter.format(this.costPerDevPerMonth / 100)
  }

  displayCostPerMonth() {
    this.costPerMonthDisplay.textContent = currencyFormatter.format(this.costPerMonth / 100)
  }

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    this.usageHoursPerDevInput.value = ''
    this.storedCountInput.value = ''
    this.projectSizeInput.value = ''
    this.developerCountInput.value = ''
    if (this.selectedMachineSize) {
      this.selectedMachineSize.closest('label')!.classList.remove('gradient-border')
      this.selectedMachineSize.checked = false
    }
    this.costPerDevPerMonth = 0
    this.costPerMonth = 0
  }

  getSummaryForCSV() {
    // headers: ['Service', 'Configuration Summary', 'Notes', 'Cost Per Month']

    return [
      [
        'Codespaces',
        `"Machine Type: ${this.machineSizeLabel}, Number of Developers: ${this.developerCount}, Usage Hours Per Dev: ${this.usageHoursPerDev}, Stored Codespaces Per Dev: ${this.storedCount}, Project Size (GB): ${this.projectSize}"`,
        `"Cost Per Developer Per Month: ${currencyFormatter.format(this.costPerDevPerMonth / 100)}"`,
        `"${currencyFormatter.format(this.costPerMonth / 100)}"`,
      ],
    ]
  }

  static observedAttributes = ['data-cost-per-dev-per-month', 'data-cost-per-month', 'data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return
    }

    if (name === 'data-cost-per-dev-per-month') {
      this.displayCostPerDevPerMonth()
      this.updatePerMonth()
    }

    if (name === 'data-cost-per-month' || name === 'data-active') {
      this.displayCostPerMonth()
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'codespacesTotal', amount: this.costPerMonth, active: this.active},
        }),
      )
    }

    if (name === 'data-cost-per-month' || name === 'data-cost-per-dev-per-month') {
      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'codespaces',
            resetEnabled: Boolean(this.costPerDevPerMonth) || Boolean(this.costPerMonth),
          },
        }),
      )
    }
  }
}
